import React, { FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../app/store';
import { LineItemType, resetLineItems } from '../../../slices/lineItemsSlice/lineItemsSlice';
import { useNavigate } from 'react-router';
import { ItemsType, resetItems } from '../../../slices/itemsSlice/itemsSlice';
import { CheckoutCartProps } from './CheckoutCartProps';
import CheckoutLineItemTile from './LineItem/CheckoutLineItemTile';
import { updateOrderAction } from '../../../actions/updateOrderAction';
import { useJwtHeaders } from '../../../hooks/useJwtHeaders';
import { calculateTotal } from '../../../helpers/calculateTotal';
import Optional from '../../Optional/Optional';
import { discountTotal, getDisplayTotal } from '../../../helpers/getDisplayTotal';
import { resetCustomerList } from '../../../slices/customerListSlice/customerListSlice';
import { resetCustomer } from '../../../slices/customerSlice/customerSlice';
import {
  resetDiscount,
  resetMordy,
  resetNote,
  resetOrder,
  resetVoucher,
} from '../../../slices/orderSlice/orderSlice';

const CheckoutCart: FC<CheckoutCartProps> = ({ order, setTotal, payType }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const headers = useJwtHeaders();
  const lineItems = useSelector<RootState, LineItemType[]>(state => state.lineItems);
  const { esrogs, extras, grades, lulavs, hadasims, aravots } = useSelector<RootState, ItemsType>(
    state => state.items
  );
  const { voucher, discount, mordy, paymentType } = order;

  const isTaxable = () => {
    const taxableTypes = ['credit card', 'quick pay', 'check'];
    // @ts-ignore
    return taxableTypes.includes(payType) || taxableTypes.includes(paymentType);
  };

  const linesTotal = calculateTotal(lineItems, 'lineTotal');

  const orderTotal = () => {
    const total = getDisplayTotal(order, linesTotal!);
    setTotal(total);
    return total;
  };

  const tax = () =>
    isTaxable() ? Math.round(getDisplayTotal(order, linesTotal!) * 0.1025 * 100) / 100 : 0;

  const handleEditClick = () => {
    dispatch(updateOrderAction(order.id!, 'open', headers, navigate));
    navigate(`/order?id=${order.id}`);
  };

  const handleHomeClick = () => {
    dispatch(resetCustomerList());
    dispatch(resetCustomer());
    dispatch(resetOrder());
    dispatch(resetItems());
    dispatch(resetLineItems());
    dispatch(resetNote());
    dispatch(resetVoucher());
    dispatch(resetDiscount());
    dispatch(resetMordy());
    navigate('/home');
  };

  const handleCompleteClick = () => {
    dispatch(
      updateOrderAction(order.id!, 'paid', headers, navigate, order.paymentType!, orderTotal(), tax())
    );
  };

  const cartedEsrogs = lineItems.filter(esrog => esrog.esrogId);
  const cartedLulavs = lineItems.filter(lulav => lulav.lulavId);
  const cartedHadasim = lineItems.filter(hadasim => hadasim.hadasimId);
  const cartedAravos = lineItems.filter(aravos => aravos.aravotId);
  const cartedExtras = lineItems.filter(extra => extra.extraId);

  return (
    <div className="card cart">
      <div className="card-header">
        <b>Cart</b>
      </div>
      <div className="card-body">
        <CheckoutLineItemTile type="esrog" items={esrogs} grades={grades} cartedItems={cartedEsrogs} />
        <CheckoutLineItemTile type="lulav" items={lulavs} cartedItems={cartedLulavs} />
        <CheckoutLineItemTile type="hadasim" items={hadasims} cartedItems={cartedHadasim} />
        <CheckoutLineItemTile type="aravot" items={aravots} cartedItems={cartedAravos} />
        <CheckoutLineItemTile type="extra" items={extras} cartedItems={cartedExtras} />
      </div>
      <Optional renderIf={voucher?.id || discount?.id || mordy?.id || isTaxable()}>
        <div className="card-header prices">
          <b>Subtotal ${linesTotal}</b>
        </div>
      </Optional>
      <Optional renderIf={voucher?.id || discount?.id || mordy?.id}>
        <div className="card-header prices">
          <b>Deducations ${discountTotal(order)}</b>
        </div>
      </Optional>
      <Optional renderIf={isTaxable()}>
        <div className="card-header prices">
          <b>Tax ${tax()}</b>
        </div>
      </Optional>
      <div className="card-header prices">
        <b>Total ${orderTotal() + tax()}</b>
      </div>
      <div className="button-container">
        <button type="button" className="btn btn-success pull-left" onClick={handleEditClick}>
          Edit Order
        </button>
        {order.status === 'open' ? (
          <button
            type="button"
            className="btn btn-primary pull-right"
            disabled={!order.paymentType}
            onClick={handleCompleteClick}>
            {!order.paymentType ? 'Choose Payment Type' : 'Complete Order'}
          </button>
        ) : (
          <button type="button" className="btn btn-danger pull-right" onClick={handleHomeClick}>
            Order Paid::Go to Home Screen
          </button>
        )}
      </div>
    </div>
  );
};

export default CheckoutCart;
